import React, { useCallback, useEffect, useState } from 'react';
import { Card, Space, Typography, Tag, Button, Descriptions, Tooltip } from 'antd';
import { normalizeBase } from '../utils';

const { Title, Text } = Typography;

type SyncInfo = {
  status: number;
  ok: boolean;
  date?: string;
  image?: string;
  digest?: string;
  release?: string;
};

export default function K8sSyncStatusPanel({ apiBase }: { apiBase: string }) {
  const [info, setInfo] = useState<SyncInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setError(null);
    setInfo(null);
    if (apiBase == null) return;
    try {
      setLoading(true);
      const base = normalizeBase(apiBase);
      const res = await fetch(`${base}/api/health/ready`, { method: 'GET' });
      const image =
        res.headers.get('x-app-image') || res.headers.get('x-container-image') || undefined;
      const digest =
        res.headers.get('x-image-digest') || res.headers.get('docker-content-digest') || undefined;
      const release =
        res.headers.get('x-release') ||
        res.headers.get('x-app-version') ||
        res.headers.get('etag') ||
        undefined;
      const date = res.headers.get('date') || undefined;
      setInfo({ status: res.status, ok: res.ok, image, digest, release, date });
    } catch (e) {
      setError((e as Error)?.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    void run();
  }, [run]);

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            k8s Sync Status
          </Title>
          <Tooltip title="Reads headers from /api/health/ready if exposed by the API">
            <Text type="secondary">(image/digest if available)</Text>
          </Tooltip>
        </Space>
      }
      extra={
        <Button onClick={() => run()} loading={loading}>
          Check
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      {apiBase == null && <Text type="secondary">API base not configured.</Text>}
      {error && <Text type="warning">{error}</Text>}
      {info && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="HTTP">
            <Space>
              <Tag color={info.ok ? 'green' : 'red'}>{info.ok ? 'OK' : `HTTP ${info.status}`}</Tag>
              {info.date ? <Text type="secondary">{info.date}</Text> : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Image">{info.image || 'not exposed'}</Descriptions.Item>
          <Descriptions.Item label="Digest">{info.digest || 'not exposed'}</Descriptions.Item>
          <Descriptions.Item label="Release">{info.release || 'not exposed'}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}
